import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { usePortfolio } from '../context/PortfolioContext';
import './Admin.css';

export default function Admin() {
  const navigate = useNavigate();
  const { data: globalData, loading: dataLoading, savePortfolioData } = usePortfolio();
  
  const [data, setData] = useState(null);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (globalData) {
      setData(globalData);
    }
  }, [globalData]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      console.error(err);
      setLoginError(err.message || 'Invalid email or password.');
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await savePortfolioData(data);
      alert('Changes successfully saved to live database!');
    } catch (error) {
      alert('Failed to save changes. Make sure your Firebase rules allow writes.');
    } finally {
      setIsSaving(false);
    }
  };

  if (authLoading || dataLoading) {
    return <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-dark)', color: 'white' }}>Loading...</div>;
  }

  // --- LOGIN SCREEN ---
  if (!user) {
    return (
      <div className="admin-page page-enter">
        <Navbar />
        <div className="admin-container" style={{ maxWidth: '400px', textAlign: 'center', minHeight: '60vh' }}>
          <h1>Admin Login</h1>
          <p style={{ marginBottom: '20px' }}>Sign in to manage your portfolio.</p>
          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <input 
              type="email" 
              placeholder="Email" 
              value={email} 
              onChange={e => setEmail(e.target.value)} 
              required
              style={{ padding: '12px', borderRadius: '8px', border: '1px solid #333', background: '#1a1a1a', color: '#fff' }}
            />
            <input 
              type="password" 
              placeholder="Password" 
              value={password} 
              onChange={e => setPassword(e.target.value)} 
              required
              style={{ padding: '12px', borderRadius: '8px', border: '1px solid #333', background: '#1a1a1a', color: '#fff' }}
            />
            {loginError && <p style={{ color: '#ef4444', fontSize: '14px' }}>{loginError}</p>}
            <button type="submit" className="btn-primary">Login</button>
          </form>
        </div>
        <Footer />
      </div>
    );
  }

  if (!data) return <div>Loading Data...</div>;

  // --- ADMIN DASHBOARD ---
  return (
    <div className="admin-page page-enter">
      <Navbar />
      
      <div className="admin-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
          <div>
            <h1>Portfolio Admin Panel</h1>
            <p>Welcome, {user.email}. Make changes below and save to update your live site instantly.</p>
          </div>
          <button className="btn-outline" onClick={handleLogout} style={{ padding: '8px 16px', fontSize: '14px' }}>Logout</button>
        </div>
        
        <div className="admin-actions" style={{ marginTop: '30px' }}>
          <button className="btn-primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Changes to Live Site'}
          </button>
        </div>

        {/* --- CV Link --- */}
        <section className="admin-section">
          <h2>CV Download Link</h2>
          <div className="form-group">
            <label>Link (e.g. /cv.pdf or Google Drive link)</label>
            <input 
              type="text" 
              value={data.cvLink} 
              onChange={e => setData({...data, cvLink: e.target.value})}
            />
          </div>
        </section>

        {/* --- Skills --- */}
        <section className="admin-section">
          <h2>Skills (About Page)</h2>
          {data.skills.map((skill, index) => (
            <div key={skill.id || index} className="admin-card">
              <input 
                type="text" 
                value={skill.name} 
                onChange={e => {
                  const newSkills = [...data.skills];
                  newSkills[index].name = e.target.value;
                  setData({...data, skills: newSkills});
                }} 
              />
              <textarea 
                value={skill.desc} 
                onChange={e => {
                  const newSkills = [...data.skills];
                  newSkills[index].desc = e.target.value;
                  setData({...data, skills: newSkills});
                }} 
              />
              <button className="btn-delete" onClick={() => {
                const newSkills = data.skills.filter((_, i) => i !== index);
                setData({...data, skills: newSkills});
              }}>Delete</button>
            </div>
          ))}
          <button className="btn-add" onClick={() => {
            setData({...data, skills: [...data.skills, { id: Date.now().toString(), name: 'New Skill', desc: 'Description', iconType: 'arch' }]})
          }}>+ Add Skill</button>
        </section>

        {/* --- Projects --- */}
        <section className="admin-section">
          <h2>Projects</h2>
          {data.projects.map((proj, index) => (
            <div key={proj.id || index} className="admin-card">
              <input placeholder="Title" value={proj.title} onChange={e => { const newP = [...data.projects]; newP[index].title = e.target.value; setData({...data, projects: newP})}} />
              <input placeholder="Link" value={proj.link} onChange={e => { const newP = [...data.projects]; newP[index].link = e.target.value; setData({...data, projects: newP})}} />
              <input placeholder="Image URL (e.g. /images/proj.png)" value={proj.image || ''} onChange={e => { const newP = [...data.projects]; newP[index].image = e.target.value; setData({...data, projects: newP})}} />
              <textarea placeholder="Description" value={proj.desc} onChange={e => { const newP = [...data.projects]; newP[index].desc = e.target.value; setData({...data, projects: newP})}} />
              <button className="btn-delete" onClick={() => {
                const newP = data.projects.filter((_, i) => i !== index);
                setData({...data, projects: newP});
              }}>Delete</button>
            </div>
          ))}
          <button className="btn-add" onClick={() => {
            setData({...data, projects: [...data.projects, { id: Date.now(), title: 'New Project', desc: '', tags: [], link: '', image: '', gradient: 'linear-gradient(135deg, #111, #333)' }]})
          }}>+ Add Project</button>
        </section>

        {/* --- Work Experience --- */}
        <section className="admin-section">
          <h2>Work Experience</h2>
          {data.experience.map((exp, index) => (
            <div key={exp.id || index} className="admin-card">
              <input placeholder="Date (e.g. 2024 - Present)" value={exp.date} onChange={e => { const newE = [...data.experience]; newE[index].date = e.target.value; setData({...data, experience: newE})}} />
              <input placeholder="Role" value={exp.role} onChange={e => { const newE = [...data.experience]; newE[index].role = e.target.value; setData({...data, experience: newE})}} />
              <textarea placeholder="Description" value={exp.desc} onChange={e => { const newE = [...data.experience]; newE[index].desc = e.target.value; setData({...data, experience: newE})}} />
              <button className="btn-delete" onClick={() => {
                const newE = data.experience.filter((_, i) => i !== index);
                setData({...data, experience: newE});
              }}>Delete</button>
            </div>
          ))}
          <button className="btn-add" onClick={() => {
            setData({...data, experience: [...data.experience, { id: Date.now().toString(), date: 'Date', role: 'Role', desc: 'Description' }]})
          }}>+ Add Experience</button>
        </section>

      </div>

      <Footer />
    </div>
  );
}
